/*
 * Runtime strings for every surface the user sees: notification messages and
 * the picker dialog.
 *
 * Manifest strings (add-in name, ribbon labels, supertips) are localized
 * separately through localizationInfo in manifest.json - see nl.json.
 *
 * Parameterized strings are functions rather than templates with placeholders,
 * so plural forms stay inside the language that needs them and the compiler
 * catches a translation that forgets an argument.
 */

export type LanguageTag = "en" | "nl";

export interface Strings {
  /** Decimal mark used when formatting sizes. */
  decimalSeparator: string;
  /** Fallback stem for the generated archive. */
  defaultArchiveName: string;

  // Shared between both commands.
  tooLargeToAttach(size: string): string;
  attaching(name: string): string;

  // Zip Attachments.
  composeOnlyZip: string;
  nothingToZip: string;
  readingAttachment(index: number, total: number): string;
  noAttachmentsReadable: string;
  compressingCount(count: number): string;
  compressingPercent(percent: number): string;
  zipResult(count: number, name: string, size: string, savedPercent: number): string;
  skippedNotice(inlineCount: number, cloudCount: number): string;
  zipFailed(reason: string): string;

  // Attach as ZIP.
  composeOnlyAttach: string;
  receiving(name: string): string;
  attached(name: string, size: string): string;
  attachFailed(reason: string): string;
  incompleteTransfer: string;

  // Picker dialog.
  dialogTitle: string;
  dropFiles: string;
  browseHint: string;
  noFilesSelected: string;
  fileSummary(count: number, size: string): string;
  archiveNameLabel: string;
  cancel: string;
  attachZip: string;
  removeFile(name: string): string;
  readingFile(name: string): string;
  sendingPercent(percent: number): string;
  done: string;
  archiveTooLarge(size: string, max: string): string;
  couldNotReadFile(name: string): string;
  transferStalled: string;
}

const en: Strings = {
  decimalSeparator: ".",
  defaultArchiveName: "attachments",

  tooLargeToAttach: (size) => `The archive is ${size}, which is too large for Outlook to attach.`,
  attaching: (name) => `Attaching ${name}…`,

  composeOnlyZip: "Open a message in compose mode to zip its attachments.",
  nothingToZip: "This message has no attachments that can be zipped.",
  readingAttachment: (index, total) => `Reading attachment ${index} of ${total}…`,
  noAttachmentsReadable: "None of the attachments could be read.",
  compressingCount: (count) => `Compressing ${count} attachment${count === 1 ? "" : "s"}…`,
  compressingPercent: (percent) => `Compressing… ${percent}%`,
  zipResult: (count, name, size, savedPercent) =>
    `Zipped ${count} attachment${count === 1 ? "" : "s"} into ${name} ` +
    `(${size}, ${savedPercent > 0 ? `${savedPercent}% smaller` : "no size gain"}).`,
  skippedNotice: (inlineCount, cloudCount) => {
    const parts: string[] = [];
    if (inlineCount > 0) parts.push(`${inlineCount} inline image${inlineCount === 1 ? "" : "s"}`);
    if (cloudCount > 0) parts.push(`${cloudCount} cloud link${cloudCount === 1 ? "" : "s"}`);
    return parts.length > 0 ? ` Left ${parts.join(" and ")} untouched.` : "";
  },
  zipFailed: (reason) => `Could not zip the attachments: ${reason}`,

  composeOnlyAttach: "Open a message in compose mode to attach files as a zip.",
  receiving: (name) => `Receiving ${name}…`,
  attached: (name, size) => `Attached ${name} (${size}).`,
  attachFailed: (reason) => `Could not attach the zip: ${reason}`,
  incompleteTransfer: "The archive did not transfer completely.",

  dialogTitle: "Attach as ZIP",
  dropFiles: "Drop files here",
  browseHint: "or click to browse",
  noFilesSelected: "No files selected.",
  fileSummary: (count, size) =>
    `${count} file${count === 1 ? "" : "s"}, ${size} before compression.`,
  archiveNameLabel: "Archive name",
  cancel: "Cancel",
  attachZip: "Attach ZIP",
  removeFile: (name) => `Remove ${name}`,
  readingFile: (name) => `Reading ${name}…`,
  sendingPercent: (percent) => `Sending to Outlook… ${percent}%`,
  done: "Done.",
  archiveTooLarge: (size, max) =>
    `The archive is ${size}, which is more than Outlook can attach (about ${max}). ` +
    `Remove a few files and try again.`,
  couldNotReadFile: (name) => `Could not read ${name}.`,
  transferStalled: "The transfer stopped responding. Please try again.",
};

const nl: Strings = {
  decimalSeparator: ",",
  defaultArchiveName: "bijlagen",

  tooLargeToAttach: (size) => `Het archief is ${size} en daarmee te groot om toe te voegen.`,
  attaching: (name) => `${name} toevoegen…`,

  composeOnlyZip: "Open een bericht in de opstelmodus om de bijlagen te zippen.",
  nothingToZip: "Dit bericht heeft geen bijlagen die gezipt kunnen worden.",
  readingAttachment: (index, total) => `Bijlage ${index} van ${total} lezen…`,
  noAttachmentsReadable: "Geen van de bijlagen kon worden gelezen.",
  compressingCount: (count) => `${count} bijlage${count === 1 ? "" : "n"} comprimeren…`,
  compressingPercent: (percent) => `Comprimeren… ${percent}%`,
  zipResult: (count, name, size, savedPercent) =>
    `${count} bijlage${count === 1 ? "" : "n"} gezipt in ${name} ` +
    `(${size}, ${savedPercent > 0 ? `${savedPercent}% kleiner` : "geen besparing"}).`,
  skippedNotice: (inlineCount, cloudCount) => {
    const parts: string[] = [];
    if (inlineCount > 0)
      parts.push(`${inlineCount} inline afbeelding${inlineCount === 1 ? "" : "en"}`);
    if (cloudCount > 0) parts.push(`${cloudCount} cloudkoppeling${cloudCount === 1 ? "" : "en"}`);
    return parts.length > 0 ? ` ${parts.join(" en ")} overgeslagen.` : "";
  },
  zipFailed: (reason) => `Zippen van de bijlagen is mislukt: ${reason}`,

  composeOnlyAttach: "Open een bericht in de opstelmodus om bestanden als zip toe te voegen.",
  receiving: (name) => `${name} ontvangen…`,
  attached: (name, size) => `${name} toegevoegd (${size}).`,
  attachFailed: (reason) => `De zip kon niet worden toegevoegd: ${reason}`,
  incompleteTransfer: "Het archief is niet volledig overgedragen.",

  dialogTitle: "Als zip toevoegen",
  dropFiles: "Sleep bestanden hierheen",
  browseHint: "of klik om te bladeren",
  noFilesSelected: "Geen bestanden geselecteerd.",
  fileSummary: (count, size) =>
    `${count} bestand${count === 1 ? "" : "en"}, ${size} vóór compressie.`,
  archiveNameLabel: "Naam archief",
  cancel: "Annuleren",
  attachZip: "Zip toevoegen",
  removeFile: (name) => `${name} verwijderen`,
  readingFile: (name) => `${name} lezen…`,
  sendingPercent: (percent) => `Verzenden naar Outlook… ${percent}%`,
  done: "Klaar.",
  archiveTooLarge: (size, max) =>
    `Het archief is ${size}, meer dan Outlook kan toevoegen (ongeveer ${max}). ` +
    `Verwijder een paar bestanden en probeer het opnieuw.`,
  couldNotReadFile: (name) => `${name} kon niet worden gelezen.`,
  transferStalled: "De overdracht reageert niet meer. Probeer het opnieuw.",
};

const catalogue: Record<LanguageTag, Strings> = { en, nl };

let active: LanguageTag = "en";

/** Maps any BCP-47 tag ("nl", "nl-NL", "nl-BE") onto a language we ship. */
export function resolveLanguage(tag: string | undefined): LanguageTag {
  return (tag || "").toLowerCase().split("-")[0] === "nl" ? "nl" : "en";
}

export function setLanguage(tag: string | undefined): LanguageTag {
  active = resolveLanguage(tag);
  return active;
}

export function language(): LanguageTag {
  return active;
}

/** The active string table. */
export function s(): Strings {
  return catalogue[active];
}
